/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation, useQuery } from 'react-query'

import type {
  BasicFailedReply,
  DeploymentGetResponse,
  DeploymentTemplateInfoV2,
  DeploymentUpdateRequest,
  DeploymentUpdateResponse,
  DeploymentsListResponse,
  ElasticsearchLogsSearchResponse,
} from '@modules/cloud-api/v1/types'
import type {
  GetDeploymentTemplatesV2UrlParams,
  SearchDeploymentEsResourceLogsUrlParams,
  UpdateDeploymentUrlParams,
} from '@modules/cloud-api/v1/urls'
import {
  getDeploymentTemplatesV2Url,
  getDeploymentUrl,
  listDeploymentsUrl,
  searchDeploymentEsResourceLogsUrl,
  searchDeploymentsUrl,
  updateDeploymentUrl,
} from '@modules/cloud-api/v1/urls'
import { fetchAsJson } from '@modules/query/helpers'
import type { QueryHookOptions } from '@modules/query/types'
import { queryClient } from '@modules/query'

// eslint-disable-next-line import/no-restricted-paths
import searchDeploymentsQuery from '@/components/StackDeploymentSearch/searchDeploymentsQuery'
// eslint-disable-next-line import/no-restricted-paths
import { transformResourceQuery } from '@/components/StackDeploymentSearch/DeploymentFilterContext/queryParser'

import type { UseMutationOptions } from 'react-query'

export const useListDeploymentsQuery = (options?: QueryHookOptions) =>
  useQuery(
    'listDeployments',
    () =>
      fetchAsJson<DeploymentsListResponse>(listDeploymentsUrl(), {
        method: 'get',
      }),
    options,
  )

export const useGetDeploymentQuery = (
  deploymentId: string | undefined,
  options?: QueryHookOptions,
) =>
  useQuery(
    ['getDeployment', deploymentId],
    () => {
      if (!deploymentId) {
        return null // sanity
      }

      return fetchAsJson<DeploymentGetResponse>(getDeploymentUrl({ deploymentId }), {
        method: 'get',
      })
    },
    { ...options, enabled: options?.enabled ?? Boolean(deploymentId) },
  )

export const useSearchOrganizationDeploymentsQuery = (
  organizationId: string,
  options?: QueryHookOptions,
) => {
  const query = searchDeploymentsQuery()

  if (query.query?.bool === undefined) {
    throw new Error('Expected organization search query to contain a bool node')
  }

  const matchOrganization = transformResourceQuery({
    path: `info.settings.metadata.organization_id`,
    queryType: `match`,
    query: { query: String(organizationId) },
  })

  query.query.bool.filter = [...(query.query.bool.filter || []), matchOrganization]

  return useQuery(
    ['searchOrganizationDeployments', organizationId],
    () =>
      fetchAsJson<DeploymentsListResponse>(searchDeploymentsUrl(), {
        method: 'post',
        body: JSON.stringify(query),
      }),
    options,
  )
}

export const useGetDeploymentTemplatesQuery = (
  params: GetDeploymentTemplatesV2UrlParams,
  options?: QueryHookOptions,
) =>
  useQuery(
    ['getDeploymentTemplates', params],
    () =>
      fetchAsJson<DeploymentTemplateInfoV2[]>(getDeploymentTemplatesV2Url(params), {
        method: 'get',
      }),
    options,
  )

export const useSearchDeploymentEsResourceLogsQuery = (
  params: SearchDeploymentEsResourceLogsUrlParams,
  options?: QueryHookOptions & { refetchInterval: number },
) =>
  useQuery(
    ['searchDeploymentEsResourceLogsUrl', ...Object.values(params)],
    () =>
      fetchAsJson<ElasticsearchLogsSearchResponse>(searchDeploymentEsResourceLogsUrl(params), {
        method: 'get',
      }),
    options,
  )

export const useUpdateDeploymentMutation = (
  options?: Omit<
    UseMutationOptions<
      DeploymentUpdateResponse,
      BasicFailedReply,
      {
        params: UpdateDeploymentUrlParams
        body: DeploymentUpdateRequest
      }
    >,
    'mutationFn'
  >,
) =>
  useMutation(
    ({ params, body }) =>
      fetchAsJson<DeploymentUpdateResponse>(
        updateDeploymentUrl({
          ...params,
        }),
        {
          method: 'put',
          body: JSON.stringify(body),
        },
      ),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        const id = data.id
        queryClient.invalidateQueries(['getDeployment', id])
        queryClient.invalidateQueries('listDeployments')
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
